import { IconButton, styled, useTheme } from '@mui/material';
import { ReactComponent as ChevronRightIcon } from '../../../../assets/icons/chevron-right.svg';

import { Typography } from '../../../../components/Typography/Typography';
import { Timesheet, TimesheetStatus, UserRole } from '../../../../types';
import { hasRole } from '../../../../utils/roles';

import { useAtomValue } from 'jotai';
import { useLocation, useNavigate } from 'react-router';
import { useTimesheetRouteId } from '../../context/TimesheetRouteIdContext';
import { ROUTES } from '../../../../constants/routes';
import { useTimesheets } from '../../../../queries/hooks/useTimesheets';
import { isSmallScreenState, userState } from '../../../../state/UIState';
import { formatWeekName } from '../../../../utils/formatters';
import { useMemo } from 'react';
import { SINGLE_TS_MOCK_DATA } from '../../../../components/Onboarding/OnboardingMockData';
import { getWeeklyTimesheetWeekStart } from '../../../../utils/getWeeklyTimesheetWeekStart';

type Props = {
  OPId?: number;
  timesheet?: Timesheet;
  isOnboarding?: boolean;
};

export function TimesheetNavigation({ OPId, timesheet, isOnboarding }: Props) {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const timesheetId = useTimesheetRouteId();
  const location = useLocation();
  const isSmallScreen = useAtomValue(isSmallScreenState);

  const isAllTimesheets = location.pathname.includes(ROUTES.ALL_TIMESHEETS);
  const isIssuedTimesheets = location.pathname.includes(ROUTES.ISSUED_TIMESHEETS);

  const currentTimesheetId = Number(timesheetId);

  const weekStart = useMemo(() => {
    if (!timesheet) return '';
    return getWeeklyTimesheetWeekStart(isOnboarding ? SINGLE_TS_MOCK_DATA : timesheet);
  }, [isOnboarding, timesheet]);

  const weekLength = useMemo(() => {
    if (!timesheet) return 0;
    const ts = isOnboarding ? SINGLE_TS_MOCK_DATA : timesheet;
    return ts.weekDates.length + ts.holidays.length;
  }, [isOnboarding, timesheet]);

  const weekName = formatWeekName(weekStart, weekLength);

  const user = useAtomValue(userState);

  // The detail route (/all-timesheets/:id) is shared between the Submitter and Proxy contexts, so we
  // infer which one applies from the OWNER of the viewed timesheet: if it is the current user's own
  // timesheet we paginate through their own list (Operating Partner dataset); otherwise it is a sheet
  // they proxy for, so we paginate through that submitter's list (Proxy dataset, filtered by OPId).
  // This keeps the prev/next arrows correct for every role combination (incl. dual Proxy+Submitter),
  // which previously broke because the own dataset has no `user` relation to match OPId against.
  const viewingOwn = !!OPId && OPId === user?.id;
  const useProxyList = !viewingOwn && hasRole(user, UserRole.PROXY);
  const roleContext = useProxyList
    ? UserRole.PROXY
    : hasRole(user, UserRole.OPERATING_PARTNER)
    ? UserRole.OPERATING_PARTNER
    : undefined;

  const { isLoading: allTimesheetsLoading, data: allTimesheets } = useTimesheets({
    userId: user?.id,
    role: roleContext,
  });

  const OPTimesheets = (allTimesheets || []).filter((ts) => ts.user?.id === OPId);
  const timesheetsIds = (useProxyList ? OPTimesheets : allTimesheets || [])
    ?.filter((ts) => {
      if (isIssuedTimesheets) {
        return ts.status === TimesheetStatus.ISSUED;
      }
      return true;
    })
    ?.map((ts) => ts.id);

  const havePrev = currentTimesheetId !== timesheetsIds[timesheetsIds.length - 1];
  const haveNext = currentTimesheetId !== timesheetsIds[0];

  const navigateRoute = isAllTimesheets
    ? ROUTES.ALL_TIMESHEETS
    : isIssuedTimesheets
    ? ROUTES.ISSUED_TIMESHEETS
    : ROUTES.ALL_TIMESHEETS;

  return (
    <WeekPaginationWrap isSmallScreen={isSmallScreen}>
      <IconButton
        disabled={!havePrev || allTimesheetsLoading}
        onClick={() => {
          const currentIndex = timesheetsIds.findIndex((element) => element === currentTimesheetId);
          const id = timesheetsIds[currentIndex + 1];
          navigate(`/${navigateRoute}/${id}`);
        }}
        sx={{
          svg: { path: { fill: colors.icon.accent, opacity: havePrev ? '1' : '0.3' } },
          backgroundColor: colors.surfaceBackground.highlighted,
          borderRadius: '4px',
          ':hover': {
            backgroundColor: colors.surfaceBackground.highlighted,
          },
        }}
      >
        <StyledPrevIcon />
      </IconButton>
      <IconButton
        sx={{
          svg: { path: { fill: colors.icon.accent, opacity: haveNext ? '1' : '0.3' } },
          backgroundColor: colors.surfaceBackground.highlighted,
          borderRadius: '4px',
          ':hover': {
            backgroundColor: colors.surfaceBackground.highlighted,
          },
        }}
        disabled={!haveNext || allTimesheetsLoading}
        onClick={() => {
          const currentIndex = timesheetsIds.findIndex((element) => element === currentTimesheetId);
          const id = timesheetsIds[currentIndex - 1];
          navigate(`/${navigateRoute}/${id}`);
        }}
      >
        <StyledNextIcon />
      </IconButton>
      <Typography
        variant='body'
        color={colors.text.main}
        style={{
          lineHeight: 'inherit',
        }}
      >
        {weekName}
      </Typography>
    </WeekPaginationWrap>
  );
}

const WeekPaginationWrap = styled('div')<{isSmallScreen: boolean}>`
  display: flex;
  justify-content: ${({isSmallScreen}) => isSmallScreen ? 'flex-start' : 'center'};
  align-items: center;
  gap: 10px;
  width: ${({isSmallScreen}) => isSmallScreen ? '100%' : '30%'};
  padding: ${({isSmallScreen}) => isSmallScreen ? '8px 0' : '0'};
`;

const StyledPrevIcon = styled(ChevronRightIcon)`
  transform: rotate(180deg);
`;

const StyledNextIcon = styled(ChevronRightIcon)``;
